import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, History, RefreshCw, Car, Gauge, Clock, Navigation as NavigationIcon } from "lucide-react";

// Custom car marker icon
const carIcon = new L.Icon({
  iconUrl: `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23E31837" stroke="%23000" stroke-width="0.5"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>')}`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

// Map center updater component
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  map.setView(center, map.getZoom());
  return null;
};

export const GPSTracking = ({
  translations,
  vehicleLocation,
  locationHistory,
  showHistory,
  locationLoading,
  fetchVehicleLocation,
  fetchLocationHistory,
  customer,
}) => {
  return (
    <div className="wallet-card mt-6" data-testid="gps-tracking-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h4 className="text-xl font-semibold text-white flex items-center gap-2">
          <MapPin className="text-lojack-red" size={22} />
          {translations.gps_title}
        </h4>
        <div className="flex gap-2">
          <button 
            onClick={fetchLocationHistory}
            className="btn-lojack-outline text-sm px-3 py-2 flex items-center gap-1"
            data-testid="history-btn"
          >
            <History size={14} />
            {translations.history}
          </button>
          <button 
            onClick={fetchVehicleLocation}
            disabled={locationLoading}
            className="btn-lojack text-sm px-3 py-2 flex items-center gap-1"
            data-testid="refresh-location-btn"
          >
            <RefreshCw size={14} className={locationLoading ? 'animate-spin' : ''} />
            {translations.refresh}
          </button>
        </div>
      </div>

      {/* Vehicle Status */}
      {vehicleLocation && (
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${vehicleLocation.status === 'moving' ? 'bg-lojack-blue-500/20' : 'bg-gray-700'}`}>
                <Car className={`w-5 h-5 ${vehicleLocation.status === 'moving' ? 'text-lojack-blue-400' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase">{translations.status}</p>
                <p className={`font-semibold ${vehicleLocation.status === 'moving' ? 'text-lojack-blue-400' : 'text-white'}`}>
                  {vehicleLocation.status === 'moving' ? translations.moving : translations.parked}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Gauge className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase">{translations.speed}</p>
                <p className="font-semibold text-white">{Math.round(vehicleLocation.speed)} km/h</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Clock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase">{translations.last_update}</p>
                <p className="font-semibold text-white text-sm">
                  {new Date(vehicleLocation.timestamp).toLocaleTimeString('it-IT')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      {vehicleLocation && (
        <div className="rounded-xl overflow-hidden border border-gray-700" style={{ height: '400px' }}>
          <MapContainer
            center={[vehicleLocation.latitude, vehicleLocation.longitude]}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <MapCenterUpdater center={[vehicleLocation.latitude, vehicleLocation.longitude]} />
            
            <Marker 
              position={[vehicleLocation.latitude, vehicleLocation.longitude]} 
              icon={carIcon}
            >
              <Popup>
                <div className="text-gray-800">
                  <strong>{customer?.vehicle_plate || 'Veicolo'}</strong><br />
                  {vehicleLocation.address}<br />
                  <span className="text-sm text-gray-600">
                    {vehicleLocation.status === 'moving' ? `${Math.round(vehicleLocation.speed)} km/h` : 'Parcheggiato'}
                  </span>
                </div>
              </Popup>
            </Marker>

            <Circle
              center={[vehicleLocation.latitude, vehicleLocation.longitude]}
              radius={100}
              pathOptions={{ 
                color: '#E31837', 
                fillColor: '#E31837', 
                fillOpacity: 0.1,
                weight: 2
              }}
            />

            {showHistory && locationHistory.length > 1 && (
              <Polyline
                positions={locationHistory.map(loc => [loc.latitude, loc.longitude])}
                pathOptions={{ color: '#E31837', weight: 3, opacity: 0.7, dashArray: '5, 10' }}
              />
            )}
          </MapContainer>
        </div>
      )}

      {/* Location details */}
      {vehicleLocation && (
        <div className="mt-4 p-4 bg-gray-800 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <NavigationIcon className="w-4 h-4 text-lojack-red" />
            <span className="text-gray-400 text-sm">{translations.current_position}</span>
          </div>
          <p className="text-white font-medium">{vehicleLocation.address}</p>
          <p className="text-gray-500 text-sm mt-1">
            {translations.coordinates}: {vehicleLocation.latitude.toFixed(6)}, {vehicleLocation.longitude.toFixed(6)}
          </p>
        </div>
      )}

      {!vehicleLocation && !locationLoading && (
        <div className="text-center py-12">
          <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">{translations.current_position}...</p>
        </div>
      )}
    </div>
  );
};
